import type { RealtimeChannel } from "@supabase/supabase-js";
import { getSupabase } from "../auth/supabaseClient";
import type { GameEventEnvelope, GameEventType, HeartbeatPayload } from "./types";
import {
  HEARTBEAT_INTERVAL_SEC,
  DISCONNECT_DETECT_SEC,
  VERSUS_SYNC_V2_ENABLED
} from "./versusConstants";

const ORDERED_GAMEPLAY_EVENT_TYPES: ReadonlySet<GameEventType> = new Set([
  "SWAP",
  "ROTATION",
  "POWERUP_BUY",
  "POWERUP_USE",
  "SPECIAL_TAP",
  "GROOVE_ACTIVATE",
  "DOOM_DEATH",
  "FORFEIT"
]);

// ── Callbacks ───────────────────────────────────────────────────────────────

export interface MatchRealtimeCallbacks {
  /** Called when a game event is received from the remote player. */
  onRemoteEvent: (event: GameEventEnvelope) => void;
  /** Called when the remote player disconnects (presence leave or heartbeat timeout). */
  onRemoteDisconnect: () => void;
  /** Called when the remote player reconnects after a disconnect. */
  onRemoteReconnect: () => void;
  /** Called for Supabase Realtime channel status changes. */
  onChannelStatusChanged: (status: string) => void;
  /** Called when a send operation fails. */
  onSendFailure: (eventType: GameEventType, reason: string) => void;
}

// ── Match Realtime Sync ─────────────────────────────────────────────────────

export class MatchRealtimeSync {
  private channel: RealtimeChannel | null = null;
  private localPlayerId: string | null = null;
  private remotePlayerId: string | null = null;
  private callbacks: MatchRealtimeCallbacks | null = null;
  private seq = 0;
  private gameplaySeq = 0;
  private heartbeatTimer: ReturnType<typeof setInterval> | null = null;
  private lastRemoteHeartbeatTs = 0;
  private disconnectCheckTimer: ReturnType<typeof setInterval> | null = null;
  private remoteDisconnected = false;
  private heartbeatPayloadFn: (() => HeartbeatPayload) | null = null;

  /**
   * Subscribe to a match's realtime channel.
   * Both players join the same channel and broadcast game events.
   * Uses both Presence (instant leave detection) and heartbeat polling
   * (8s fallback) for robust disconnect detection.
   */
  subscribe(
    matchId: string,
    localPlayerId: string,
    remotePlayerId: string,
    callbacks: MatchRealtimeCallbacks,
    heartbeatPayloadFn: () => HeartbeatPayload
  ): void {
    this.unsubscribe();

    this.localPlayerId = localPlayerId;
    this.remotePlayerId = remotePlayerId;
    this.callbacks = callbacks;
    this.heartbeatPayloadFn = heartbeatPayloadFn;
    this.seq = 0;
    this.gameplaySeq = 0;
    this.remoteDisconnected = false;
    this.lastRemoteHeartbeatTs = Date.now();

    const supabase = getSupabase();
    const channel = supabase.channel(`bb:match:${matchId}`);

    // ── Listen for all game events ──────────────────────────────────────
    channel.on("broadcast", { event: "game_event" }, ({ payload }) => {
      const envelope = payload as GameEventEnvelope;
      if (this.localPlayerId == null || this.remotePlayerId == null) return;
      // Strict trust boundary: only accept packets from the expected remote player.
      if (envelope.playerId !== this.remotePlayerId) return;
      if (envelope.playerId === this.localPlayerId) return;

      // Any event from the remote player resets the heartbeat timer
      this.lastRemoteHeartbeatTs = Date.now();
      if (this.remoteDisconnected) {
        this.remoteDisconnected = false;
        this.callbacks?.onRemoteReconnect();
      }

      this.callbacks?.onRemoteEvent(envelope);
    });

    // ── Presence: instant disconnect/reconnect detection ─────────────────
    channel.on("presence", { event: "leave" }, ({ leftPresences }) => {
      const remoteLeft = leftPresences.some(
        (p: Record<string, unknown>) => p.player_id === this.remotePlayerId
      );
      if (remoteLeft && !this.remoteDisconnected) {
        this.remoteDisconnected = true;
        this.callbacks?.onRemoteDisconnect();
      }
    });

    channel.on("presence", { event: "join" }, ({ newPresences }) => {
      const remoteJoined = newPresences.some(
        (p: Record<string, unknown>) => p.player_id === this.remotePlayerId
      );
      if (remoteJoined && this.remoteDisconnected) {
        this.lastRemoteHeartbeatTs = Date.now();
        this.remoteDisconnected = false;
        this.callbacks?.onRemoteReconnect();
      }
    });

    void channel.subscribe(async (status) => {
      this.callbacks?.onChannelStatusChanged(status);
      if (status === "SUBSCRIBED") {
        // Track this player's presence
        await channel.track({ player_id: localPlayerId });
        // Start heartbeat loop and fallback disconnect check
        this.startHeartbeatLoop();
        this.startDisconnectCheck();
        return;
      }
      if (
        (status === "CHANNEL_ERROR" || status === "TIMED_OUT" || status === "CLOSED")
        && !this.remoteDisconnected
      ) {
        this.remoteDisconnected = true;
        this.callbacks?.onRemoteDisconnect();
      }
    });

    this.channel = channel;
  }

  /** Unsubscribe from the match channel and clean up all timers. */
  unsubscribe(): void {
    this.stopHeartbeatLoop();
    this.stopDisconnectCheck();

    if (this.channel != null) {
      void this.channel.untrack();
      void this.channel.unsubscribe();
      const supabase = getSupabase();
      void supabase.removeChannel(this.channel);
      this.channel = null;
    }
    this.localPlayerId = null;
    this.remotePlayerId = null;
    this.callbacks = null;
    this.heartbeatPayloadFn = null;
    this.seq = 0;
    this.gameplaySeq = 0;
    this.remoteDisconnected = false;
  }

  /** Send a game event to the remote player. Auto-increments seq. */
  sendEvent(type: GameEventType, payload: unknown): GameEventEnvelope | null {
    if (this.channel == null || this.localPlayerId == null) return null;
    const nextSeq = ++this.seq;
    const sentAtMs = Date.now();
    const envelope: GameEventEnvelope = {
      protocolVersion: VERSUS_SYNC_V2_ENABLED ? 2 : 1,
      eventId: `${this.localPlayerId}:${nextSeq}`,
      sentAtMs,
      type,
      seq: nextSeq,
      ts: sentAtMs,
      playerId: this.localPlayerId,
      payload
    };
    if (ORDERED_GAMEPLAY_EVENT_TYPES.has(type)) {
      envelope.gameplaySeq = ++this.gameplaySeq;
    }
    void this.channel.send({
      type: "broadcast",
      event: "game_event",
      payload: envelope
    }).then((result) => {
      if (result !== "ok") {
        this.callbacks?.onSendFailure(type, String(result));
      }
    }).catch((err: unknown) => {
      const reason = err instanceof Error ? err.message : String(err);
      this.callbacks?.onSendFailure(type, reason);
    });
    return envelope;
  }

  isConnected(): boolean {
    return this.channel != null;
  }

  // ── Heartbeat loop ──────────────────────────────────────────────────────

  private startHeartbeatLoop(): void {
    this.stopHeartbeatLoop();
    // Send first heartbeat immediately
    if (this.heartbeatPayloadFn != null) {
      this.sendEvent("HEARTBEAT", this.heartbeatPayloadFn());
    }
    this.heartbeatTimer = setInterval(() => {
      if (this.heartbeatPayloadFn != null) {
        this.sendEvent("HEARTBEAT", this.heartbeatPayloadFn());
      }
    }, HEARTBEAT_INTERVAL_SEC * 1000);
  }

  private stopHeartbeatLoop(): void {
    if (this.heartbeatTimer != null) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
  }

  // ── Disconnect detection (heartbeat fallback) ──────────────────────────
  // Presence gives instant detection when the browser tab closes, but
  // heartbeat polling catches cases where the Supabase connection degrades
  // without a clean Presence leave event.

  private startDisconnectCheck(): void {
    this.stopDisconnectCheck();
    this.lastRemoteHeartbeatTs = Date.now();
    this.disconnectCheckTimer = setInterval(() => {
      const elapsed = (Date.now() - this.lastRemoteHeartbeatTs) / 1000;
      if (elapsed >= DISCONNECT_DETECT_SEC && !this.remoteDisconnected) {
        this.remoteDisconnected = true;
        this.callbacks?.onRemoteDisconnect();
      }
    }, 1000);
  }

  private stopDisconnectCheck(): void {
    if (this.disconnectCheckTimer != null) {
      clearInterval(this.disconnectCheckTimer);
      this.disconnectCheckTimer = null;
    }
  }
}
